import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { TodayView } from './dashboard/TodayView';
import { StatsView } from './dashboard/StatsView';

type Tab = 'today' | 'stats';
const TAB_KEY = 'blast-crm-dashboard-tab';

/**
 * Dashboard - the landing page after login. A thin shell with a Today | Stats
 * toggle: "Today" is the action-first morning view; "Stats" is the analytics
 * cockpit (preserved verbatim). The choice is remembered per device.
 */
export const Dashboard = () => {
  const { user } = useAuthStore();
  const [tab, setTab] = useState<Tab>(() => (localStorage.getItem(TAB_KEY) as Tab) || 'today');

  const select = (t: Tab) => {
    setTab(t);
    localStorage.setItem(TAB_KEY, t);
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div
        role="radiogroup"
        aria-label="Dashboard view"
        className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-white dark:bg-gray-800"
      >
        {(
          [
            ['today', 'Today'],
            ['stats', 'Performance'],
          ] as [Tab, string][]
        ).map(([val, label]) => (
          <button
            key={val}
            type="button"
            role="radio"
            aria-checked={tab === val}
            onClick={() => select(val)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              tab === val
                ? 'bg-blast-50 text-blast-700 dark:bg-blast-950 dark:text-blast-300'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'today' ? <TodayView /> : <StatsView />}
    </div>
  );
};
